import {
  ENDPOINT_CLUSTERS_BY_ID,
  ENDPOINT_CROP_GENERATIONS,
  ENDPOINT_CROP_TYPES,
  ENDPOINT_PARTIES,
  ENDPOINT_SELECT_PARTIES,
} from '@/common/constants';
import { HarvestType } from '@/common/constants/harvest.constants';
import {
  PartiesEnum,
  StorageTypeEnum,
} from '@/common/constants/parties.constants';
import { HttpApi } from '@/common/http';
import i18n from '@/i18n';
import { create } from 'zustand';
import {
  IClusterData,
  ICropData,
  ICropGeneration,
  ICropType,
  IPartiesAction,
  IPartiesState,
  IParty,
  IPartyResponse,
  SelectParties,
} from '../models';

const httpApi = new HttpApi();
export const usePartyStore = create<IPartiesState & IPartiesAction>((set) => ({
  parties: null,
  partiesSelectGeneral: [],
  partiesLoading: true,
  isLoading: false,
  party: [],
  partyLoading: true,
  cropTypes: [],
  clusters: [],
  cropData: {} as ICropData,
  partyDetail: null,
  generationTypes: [
    {
      value: PartiesEnum.SEED,
      label: i18n.t(`parties.generation.${PartiesEnum.SEED}`)!,
    },
    {
      value: PartiesEnum.TECHNICAL,
      label: i18n.t(`parties.generation.${PartiesEnum.TECHNICAL}`)!,
    },
  ],
  storageTypes: [
    {
      value: StorageTypeEnum.MAIN,
      label: 'Asosiy',
    },
    {
      value: StorageTypeEnum.RESERVE,
      label: 'Zaxira',
    },
  ],
  cropGenerations: [],
  industrialGrades: Array.from({ length: 5 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })),
  classList: Array.from({ length: 3 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })),
  setPartyDetail: (party) =>
    set(() => ({
      partyDetail: party,
    })),
  fetchCropTypes: async () => {
    try {
      const response = await httpApi.get<ICropType[]>(ENDPOINT_CROP_TYPES);
      set({
        cropTypes: response?.map((el) => ({
          value: String(el.id),
          label: el.name,
        })),
      });
    } catch (error) {
      console.error('Failed to fetch parties:', error);
      throw error;
    }
  },
  fetchClusters: async () => {
    try {
      const response = await httpApi.get<IClusterData[]>(
        ENDPOINT_CLUSTERS_BY_ID,
      );
      set({
        clusters: response?.map(({ id, name, ...rest }) => {
          return {
            value: id,
            label: name,
            ...rest,
          };
        }),
      });
    } catch (error) {
      console.error('Failed to fetch parties:', error);
      throw error;
    }
  },
  fetchCropTypesById: async (id: string) => {
    try {
      const response = await httpApi.get<ICropData>(
        ENDPOINT_CROP_TYPES + `/${id}`,
      );
      set({
        cropData: response,
      });
    } catch (error) {
      console.error('Failed to fetch parties:', error);
      throw error;
    }
  },
  fetchParties: async (params?: Record<string, string | number>) => {
    set({ partiesLoading: true });
    try {
      const response = await httpApi.get<IPartyResponse>(
        ENDPOINT_PARTIES,
        params,
      );
      set({
        parties: {
          total: response?.total,
          content: response?.content,
        },
        partiesPagination: {
          totalElements: response?.totalElements,
          totalPages: response?.totalPages,
          page: response.page,
          size: response?.limit!,
        },
      });
    } catch (error) {
      console.error('Failed to fetch parties:', error);
      throw error;
    } finally {
      setTimeout(() => {
        set({ partiesLoading: false });
      }, 300);
    }
  },
  fetchPartiesSelect: async (
    crop_type_id?: string | undefined,
    harvest_type?:
      | HarvestType.COMBINE
      | HarvestType.GROUND_HARVESTED
      | HarvestType.MANUAL
      | string
      | undefined,
  ) => {
    try {
      const response = await httpApi.get<SelectParties[]>(
        ENDPOINT_SELECT_PARTIES,
        {
          crop_type_id,
          harvest_type,
        },
      );
      set({
        partiesSelectGeneral: response,
      });
    } catch (e) {
      console.error('Failed to fetch parties:', e);
      throw e;
    }
  },
  fetchParty: async (id: string) => {
    return await httpApi.get<IParty>(ENDPOINT_PARTIES + '/' + id);
  },
  fetchPartyByCrop: async (id: string) => {
    set({ partyLoading: true });
    const response = await httpApi.get<IParty[]>(ENDPOINT_PARTIES, {
      crop_type_id: id,
    });
    set({ party: response });
    setTimeout(() => {
      set({ partyLoading: false });
    }, 300);
  },
  fetchCropGenerations: async (type?: string) => {
    const response = await httpApi.get<ICropGeneration[]>(
      ENDPOINT_CROP_GENERATIONS,
      { type },
    );
    set({
      cropGenerations: response?.map((el) => ({
        value: String(el.id),
        label: el.name,
      })),
    });
  },
  addParty: async (party: IParty) => {
    set({ isLoading: true });
    try {
      await httpApi.post(ENDPOINT_PARTIES, party);
    } catch (error) {
      set({ isLoading: false });
      console.error('Failed to add party:', error);
      throw error;
    }
    setTimeout(() => {
      set({ isLoading: false });
    }, 300);
  },

  updateParty: async (id: string, data: Partial<IParty>) => {
    set({ isLoading: true });
    try {
      await httpApi.put(ENDPOINT_PARTIES + '/' + id, data);
    } catch (error) {
      console.error('Failed to update party:', error);
      throw error;
    }
    setTimeout(() => {
      set({ isLoading: false });
    }, 300);
  },

  deleteParty: async (id: string) => {
    try {
      await httpApi.delete(ENDPOINT_PARTIES + '/' + id);
    } catch (error) {
      console.error('Failed to delete party:', error);
      throw error;
    }
  },
}));
